import cuidaBg from '@cannect/assets/img/home/cuida-bg.webp'
import ctaIcon from '@cannect/assets/img/home/cuida-cta-icon.webp'
import { Typography } from '@cannect/new-components/atoms'
import { CtaPill } from '@cannect/new-components/molecules'
import { LuCheck } from 'react-icons/lu'

/**
 * Itens da checklist. O design destaca só um trecho de cada rótulo em SemiBold, e o trecho muda
 * de posição de item para item — daí a divisão em `strong` + `rest`/`lead`.
 */
const CARE_FEATURES = [
  { strong: 'Acompanhamento', rest: ' ao longo do tratamento' },
  { lead: 'Organização da ', strong: 'sua jornada em um só lugar' },
  { lead: 'Orientação sobre as', strong: ' próximas etapas' },
  { strong: 'Suporte para dúvidas', rest: ' do dia a dia' }
] as const

/**
 * Seção "Cannect Cuida" da home (Figma node 4616:245).
 *
 * A revisão de 2026-07-31 refez a seção inteira. O nó anterior foi substituído por um novo, e com
 * ele mudou tudo: o fundo claro virou uma foto full-bleed de mão segurando um celular com a
 * conversa na tela, no lugar do mockup de chat que era desenhado em HTML; o parágrafo de apoio
 * saiu; a checklist ganhou fundo translúcido sobre a foto; e sobrou um CTA só, agora com
 * ilustração. A seção foi de 687 para 763px.
 *
 * A coluna de texto tem 504px e fica à esquerda, sobre a parte escura da foto — o celular ocupa a
 * direita. Abaixo de `lg` a foto vira fundo de seção com um véu escuro por cima, para o texto
 * continuar legível quando a composição não couber mais lado a lado.
 *
 * Desvios deliberados em relação ao arquivo:
 *
 * 1. O check da checklist vem `marketplace-500` sobre círculo `marketplace-300` no design, o que
 *    rende 1,41:1 e deixa o ícone praticamente invisível. Aqui usa `marketplace-800` (4,79:1),
 *    alinhado ao mesmo elemento na seção da farmácia.
 * 2. O CTA usa a variante `solidDeep` do CtaPill, que corrige o contraste do rótulo.
 * 3. O destino: o arquivo não define. Vai para `/cuida`, a página do Cannect Cuida — a rota é
 *    registrada em `src/components/Layout/index.tsx`, não em `routes/index.tsx`.
 */
export const CannectCuidaSection = () => (
  <section aria-labelledby="cannect-cuida-heading" className="relative w-full overflow-hidden">
    <img
      src={cuidaBg}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover"
    />

    {/* Véu só abaixo de `lg`: no desktop a própria foto já é escura na coluna do texto. */}
    <span aria-hidden className="absolute inset-0 bg-marketplace-1000/70 lg:hidden" />

    <div className="relative mx-auto w-full max-w-[1230px] px-8 py-[88px]">
      <div className="flex w-full flex-col gap-10 lg:w-[504px]">
        <div className="flex flex-col gap-2">
          <p className="m-0 text-base leading-[1.6] text-neutral-50">Cannect Cuida</p>

          <Typography.Heading
            id="cannect-cuida-heading"
            level={2}
            weight="regular"
            className="m-0 text-[28px] leading-[1.2] tracking-[-0.448px] text-neutral-50">
            {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica.
                O arquivo quebra em três linhas; o `<br>` só entra a partir de `lg`. */}
            Acompanhamento para <br className="hidden lg:inline" />o tratamento não parar{' '}
            <br className="hidden lg:inline" />
            <span className="italic">no meio do caminho.</span>
          </Typography.Heading>
        </div>

        <ul className="m-0 flex list-none flex-col gap-[13px] p-0">
          {CARE_FEATURES.map((item) => (
            <li
              key={item.strong}
              className="flex items-center gap-[10px] rounded-[8px] bg-secondary-100/20 p-6 text-sm leading-[18.2px] text-neutral-50">
              <span
                aria-hidden
                className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-marketplace-300">
                <LuCheck className="h-[10px] w-[10px] text-marketplace-800" strokeWidth={3} />
              </span>
              <span>
                {'lead' in item && item.lead}
                <strong className="font-semibold">{item.strong}</strong>
                {'rest' in item && item.rest}
              </span>
            </li>
          ))}
        </ul>

        <CtaPill variant="solidDeep" title="Conhecer o Cannect Cuida" path="/cuida" icon={ctaIcon} className="w-fit" />
      </div>
    </div>
  </section>
)
